import { useCallback, useEffect, useMemo, useState } from "react";
import { useDragControls } from "framer-motion";
import { ENVIRONMENT } from "../../../../env";
import { useKeyboardHeight } from "../../../../hooks/use-keyboard-height";

const DEFAULT_MAX_CHARS = 100000;

export function useVisualViewportBottomInset() {
    const { height, open } = useKeyboardHeight();
    return { bottomInset: height, keyboardOpen: open };
}

type UseTypeArgs = {
    onClose: () => void;
    onMicClick: () => void; // ✅ NEW
    maxChars?: number;
};

const DRAFT_KEY = 'mailander_type_draft';

export const useType = ({ onClose, onMicClick, maxChars }: UseTypeArgs) => {
    const [value, setValue] = useState(() => {
        try { return localStorage.getItem(DRAFT_KEY) ?? ''; } catch { return ''; }
    });
    const { bottomInset: viewportBottomOffset, keyboardOpen } = useVisualViewportBottomInset();
    const dragControls = useDragControls();

    // Persist draft on every change
    useEffect(() => {
        try {
            if (value) localStorage.setItem(DRAFT_KEY, value);
            else localStorage.removeItem(DRAFT_KEY);
        } catch { /* ignore */ }
    }, [value]);

    const max = useMemo(() => maxChars ?? DEFAULT_MAX_CHARS, [maxChars]);
    const isSendMode = value.trim().length > 0;

    // ✅ Start drag only when handle is touched
    const startDragFromHandle = useCallback(
        (e: React.PointerEvent) => {
            // prevents focus/scroll weirdness while dragging on mobile
            e.preventDefault();
            dragControls.start(e);
        },
        [dragControls],
    );

    /**
     * ✅ Make dismiss easier (small pull closes)
     * If you want even more sensitive: dy > 24
     */
    const onSheetDragEnd = useCallback(
        (
            _: unknown,
            info: { offset: { y: number }; velocity: { y: number } },
        ) => {
            const dy = info.offset.y;
            const vy = info.velocity.y;

            const shouldClose = dy > 28 || vy > 520;
            if (shouldClose) onClose();
        },
        [onClose],
    );

    const attach = useCallback(() => {
        if (ENVIRONMENT === "DEV") console.log("Type: attach");
    }, []);

    const send = useCallback(() => {
        if (ENVIRONMENT === "DEV") console.log("Type: send", value);
        setValue("");
        // Optional: if you want to close after send, uncomment:
        // onClose();
    }, [value]);

    const record = useCallback(() => {
        if (ENVIRONMENT === "DEV") console.log("Type: microphone");

        // ✅ Switch Chat Dock -> RecordDock
        // (Dock will usually close Type before calling onMicClick, but it's OK either way)
        onMicClick();
    }, [onMicClick]);

    const recordOrSend = useCallback(() => {
        if (isSendMode) return send();
        return record();
    }, [isSendMode, send, record]);

    return {
        value,
        setValue,
        maxChars: max,
        viewportBottomOffset,
        keyboardOpen,

        // drag
        dragControls,
        startDragFromHandle,
        onSheetDragEnd,

        // actions
        attach,
        recordOrSend,
        isSendMode,
    };
};
